import { Globals } from '../../../modules/Globals';
import axios from 'axios';
import { ICaja } from '../../../interfaces/ICaja';
import { ICajaResume } from '@/interfaces/ICajaResume';
import { IInvoicingData } from '@/interfaces/IInvoicingData';
import { IInvoicingSetupJob } from '@/interfaces/IInvoicingSetupJob';

const apiUrl = Globals.apiPath + '/invoicing/afip';
const setupJobsUrl = Globals.apiPath + '/invoicing/setup-jobs';

export default class InvoicingAfipService {
  constructor() {}

  /**
   * Async setup: returns immediately with a job id; progress arrives via WS
   * `invoicingSetup` and the setup-jobs GET endpoints.
   */
  static async startSetup({
    restaurantId,
    branchId,
    name,
    cuit,
    cuitAdministrador,
    claveFiscal,
    businessCategory,
  }: {
    restaurantId: number;
    branchId: number;
    name: string;
    cuit: string;
    cuitAdministrador?: string;
    claveFiscal: string;
    businessCategory: string;
  }): Promise<{ jobPublicId: string }> {
    const res = await axios.post<{ jobPublicId: string }>(`${apiUrl}/setup`, {
      restaurantId,
      branchId,
      name,
      cuit,
      cuitAdministrador,
      claveFiscal,
      businessCategory,
    });
    return res.data;
  }

  /** Includes `invoicingData` once the job succeeded. */
  static async getSetupJob(publicId: string): Promise<IInvoicingSetupJob> {
    const res = await axios.get<IInvoicingSetupJob>(
      `${setupJobsUrl}/${publicId}`
    );
    return res.data;
  }

  /** The branch's running job for reattach-on-mount, or null. */
  static async getActiveSetupJob(
    restaurantId: number,
    branchId: number
  ): Promise<IInvoicingSetupJob | null> {
    try {
      const res = await axios.get<IInvoicingSetupJob>(
        `${setupJobsUrl}/active`,
        { params: { restaurantId, branchId } }
      );
      return res.data;
    } catch (err) {
      if (
        axios.isAxiosError(err) &&
        err.response &&
        err.response.status === 404
      ) {
        return null;
      }
      throw err;
    }
  }

  static async create({
    restaurantId,
    branchId,
    name,
    cuit,
    cuitAdministrador,
    claveFiscal,
    alias,
    businessCategory,
  }: {
    restaurantId: number;
    branchId: number;
    name: string;
    cuit: string;
    cuitAdministrador?: string;
    claveFiscal: string;
    alias: string;
    businessCategory: string;
  }): Promise<IInvoicingData> {
    let res = await axios.post<IInvoicingData>(`${apiUrl}`, {
      restaurantId,
      branchId,
      name,
      cuit,
      cuitAdministrador,
      claveFiscal,
      alias,
      businessCategory,
    });

    return res.data;
  }

  static async update({
    invoicingDataId,
    name,
    nroSalesPoint,
    businessCategory,
    autoInvoice,
    ingresosBrutos,
    inicioActividades,
  }: {
    invoicingDataId: number;
    name?: string;
    nroSalesPoint?: number;
    businessCategory?: string;
    autoInvoice?: boolean;
    ingresosBrutos?: string;
    /** ISO yyyy-mm-dd; empty string clears the field */
    inicioActividades?: string;
  }): Promise<IInvoicingData> {
    let res = await axios.put<IInvoicingData>(`${apiUrl}/${invoicingDataId}`, {
      name,
      nroSalesPoint,
      businessCategory,
      autoInvoice,
      ingresosBrutos,
      inicioActividades,
    });

    return res.data;
  }

  static async getSalesPoints({
    restaurantId,
    branchId,
    invoicingDataId,
  }: {
    restaurantId: number;
    branchId: number;
    invoicingDataId: number;
  }): Promise<any> {
    let res = await axios.get<any>(`${apiUrl}/${invoicingDataId}/salesPoints`);

    return res.data;
  }
}
